import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  CreditCard, CheckCircle, AlertTriangle, ExternalLink,
  Loader2, Calendar, Shield, Zap,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { useTranslation } from '@/i18n/TranslationContext';
import { billingAPI } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import { cn } from '@/lib/utils';

function formatDate(iso) {
  if (!iso) return 'N/A';
  try { return new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }); }
  catch { return iso; }
}

export default function BillingPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const [billing, setBilling] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState('');

  useEffect(() => {
    const fetch = async () => {
      try {
        const [statusRes, configRes] = await Promise.all([
          billingAPI.getStatus(),
          billingAPI.getConfig(),
        ]);
        setBilling(statusRes.data);
        setConfig(configRes.data);
      } catch { /* ignore */ }
      setLoading(false);
    };
    fetch();

    // Check if returning from checkout
    if (searchParams.get('session_id')) {
      toast.success('Payment processed!', { description: 'Your subscription is now active.' });
    }
  }, [searchParams]);

  const handleCheckout = async (plan) => {
    setCheckoutLoading(plan);
    try {
      const res = await billingAPI.createCheckout(plan);
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start checkout');
      setCheckoutLoading('');
    }
  };

  const handlePortal = async () => {
    try {
      const res = await billingAPI.createPortal();
      window.location.href = res.data.portal_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to open billing portal');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const plan = billing?.plan || 'free';
  const isActive = billing?.status === 'active' && plan !== 'free';
  const isPastDue = billing?.status === 'past_due';
  const role = user?.role || '';

  return (
    <div className="space-y-6 animate-fade-in max-w-4xl" data-testid="billing-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-foreground">{t('billing_subscription')}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t('manage_billing')}</p>
      </div>

      {/* Past Due Warning */}
      {isPastDue && (
        <Card className="border-vrs-amber/30 bg-vrs-amber/5">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-vrs-amber shrink-0" />
            <div>
              <p className="text-sm font-semibold text-foreground">Payment Past Due</p>
              <p className="text-xs text-muted-foreground">Your last payment failed. Please update your payment method to continue using VRS.</p>
            </div>
            <Button size="sm" onClick={handlePortal} className="shrink-0">Update Payment</Button>
          </CardContent>
        </Card>
      )}

      {/* Current Plan */}
      <Card className="border-border/60">
        <CardHeader>
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-primary" />Current Plan
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-lg font-semibold text-foreground capitalize">
                {plan === 'free' ? 'Free' : plan === 'compliance_regular' ? 'Compliance Regular' : plan === 'procurement' ? 'Procurement' : plan}
              </p>
              <Badge variant="outline" className={cn('text-[10px] mt-1',
                isActive ? 'border-vrs-green/30 text-vrs-green' :
                isPastDue ? 'border-vrs-amber/30 text-vrs-amber' :
                'text-muted-foreground'
              )}>
                {billing?.status || 'free'}
              </Badge>
            </div>
            {isActive && (
              <Button variant="outline" size="sm" onClick={handlePortal} data-testid="manage-subscription-button">
                <ExternalLink className="mr-2 h-4 w-4" />Manage Subscription
              </Button>
            )}
          </div>

          <Separator />

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-xs text-muted-foreground">{t('scans_used')}</p>
              <p className="text-lg font-bold font-mono">{billing?.scans_used_this_month || 0}</p>
              <p className="text-[10px] text-muted-foreground">
                {billing?.scans_limit === -1 ? 'Unlimited' : `of ${billing?.scans_limit || 1} this month`}
              </p>
            </div>
            {billing?.next_billing_date && (
              <div>
                <p className="text-xs text-muted-foreground">{t('next_billing')}</p>
                <p className="text-sm font-medium">{formatDate(billing.next_billing_date)}</p>
              </div>
            )}
            {billing?.payment_method_last4 && (
              <div>
                <p className="text-xs text-muted-foreground">{t('payment_method')}</p>
                <p className="text-sm font-medium flex items-center gap-1">
                  <CreditCard className="h-3 w-3" /> **** {billing.payment_method_last4}
                </p>
              </div>
            )}
            {billing?.cancel_at_period_end && (
              <div>
                <p className="text-xs text-muted-foreground">Status</p>
                <Badge variant="outline" className="text-[10px] border-vrs-amber/30 text-vrs-amber">Cancels at period end</Badge>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Available Plans (show when not subscribed or for upgrading) */}
      {(!isActive || plan === 'free') && config?.plans && (
        <div className="space-y-3">
          <h2 className="text-sm font-semibold tracking-wide uppercase text-muted-foreground">{t('available_plans')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Compliance Regular */}
            {(role === 'compliance' || role === 'super_admin' || role === 'admin') && (
              <Card className="border-border/60 hover:border-primary/30 transition-[border-color] duration-200">
                <CardContent className="p-5 space-y-3">
                  <div className="flex items-center gap-2">
                    <Shield className="h-5 w-5 text-vrs-green" />
                    <p className="text-base font-semibold">Compliance Regular</p>
                  </div>
                  <p className="text-3xl font-bold font-mono">${config.plans.compliance_regular?.price_monthly || 49}<span className="text-sm font-normal text-muted-foreground">/mo</span></p>
                  <p className="text-xs text-muted-foreground">{config.plans.compliance_regular?.description}</p>
                  <Separator />
                  <Button className="w-full" onClick={() => handleCheckout('compliance_regular')} disabled={checkoutLoading === 'compliance_regular'} data-testid="subscribe-compliance-button">
                    {checkoutLoading === 'compliance_regular' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Zap className="mr-2 h-4 w-4" />}
                    Subscribe
                  </Button>
                  <Button variant="outline" className="w-full" onClick={() => handleCheckout('one_time_scan')} disabled={checkoutLoading === 'one_time_scan'} data-testid="buy-one-time-button">
                    One-Time Scan (${config.plans.compliance_regular?.price_one_time || 19})
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Procurement */}
            {(role === 'procurement' || role === 'super_admin' || role === 'admin') && (
              <Card className="border-border/60 hover:border-primary/30 transition-[border-color] duration-200">
                <CardContent className="p-5 space-y-3">
                  <div className="flex items-center gap-2">
                    <Shield className="h-5 w-5 text-vrs-cyan" />
                    <p className="text-base font-semibold">Procurement</p>
                  </div>
                  <p className="text-3xl font-bold font-mono">${config.plans.procurement?.price_monthly || 199}<span className="text-sm font-normal text-muted-foreground">/mo</span></p>
                  <p className="text-xs text-muted-foreground">{config.plans.procurement?.description}</p>
                  <Separator />
                  <Button className="w-full" onClick={() => handleCheckout('procurement')} disabled={checkoutLoading === 'procurement'} data-testid="subscribe-procurement-button">
                    {checkoutLoading === 'procurement' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Zap className="mr-2 h-4 w-4" />}
                    Subscribe
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Regulatory (free) */}
            {role === 'regulatory' && (
              <Card className="border-vrs-green/30">
                <CardContent className="p-5 space-y-3">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-vrs-green" />
                    <p className="text-base font-semibold">Regulatory</p>
                  </div>
                  <p className="text-3xl font-bold font-mono text-vrs-green">Free</p>
                  <p className="text-xs text-muted-foreground">Free for authorized government regulators</p>
                  <Separator />
                  <Badge variant="outline" className="border-vrs-green/30 text-vrs-green text-xs">Active</Badge>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
